import { useState } from "react";
import { Input, Select, Card, Tabs, Table, Tag, Modal, Button } from "antd";
import { Send, CheckCircle2, Network, Search } from "lucide-react";
import { StatCard } from "@/components/dashboard/StatCard";

const mockChapters = [
  {
    id: 1,
    srNo: 1,
    name: "Los Angeles Downtown Chapter",
    location: "Los Angeles, California",
    activeMembers: 145,
    events: 28,
    activeSponsors: 12,
    contactName: "John Anderson",
    contactNumber: "+1 (310) 555-0123",
    requestStatus: null,
  },
  {
    id: 2,
    srNo: 2,
    name: "Manhattan Chapter",
    location: "New York City, New York",
    activeMembers: 203,
    events: 42,
    activeSponsors: 18,
    contactName: "Sarah Williams",
    contactNumber: "+1 (212) 555-0124",
    requestStatus: "sent",
  },
  {
    id: 3,
    srNo: 3,
    name: "San Francisco Tech Chapter",
    location: "San Francisco, California",
    activeMembers: 156,
    events: 35,
    activeSponsors: 15,
    contactName: "Michael Chen",
    contactNumber: "+1 (415) 555-0125",
    requestStatus: "accepted",
  },
  {
    id: 4,
    srNo: 4,
    name: "Houston Central Chapter",
    location: "Houston, Texas",
    activeMembers: 98,
    events: 22,
    activeSponsors: 10,
    contactName: "Jessica Martinez",
    contactNumber: "+1 (713) 555-0126",
    requestStatus: null,
  },
  {
    id: 5,
    srNo: 5,
    name: "Miami South Beach Chapter",
    location: "Miami, Florida",
    activeMembers: 89,
    events: 18,
    activeSponsors: 7,
    contactName: "David Rodriguez",
    contactNumber: "+1 (305) 555-0127",
    requestStatus: "sent",
  },
  {
    id: 6,
    srNo: 6,
    name: "Brooklyn Chapter",
    location: "Brooklyn, New York",
    activeMembers: 87,
    events: 16,
    activeSponsors: 8,
    contactName: "Emily Thompson",
    contactNumber: "+1 (718) 555-0128",
    requestStatus: null,
  },
  {
    id: 7,
    srNo: 7,
    name: "Austin Downtown Chapter",
    location: "Austin, Texas",
    activeMembers: 125,
    events: 30,
    activeSponsors: 11,
    contactName: "Robert Brown",
    contactNumber: "+1 (512) 555-0129",
    requestStatus: "accepted",
  },
];

const mockCollaborations = [
  {
    id: 1,
    chapter1: "Los Angeles Downtown Chapter",
    chapter2: "San Francisco Tech Chapter",
    type: "Joint Event",
    status: "ongoing" as const,
    startDate: "2024-01-15",
    events: 3,
  },
  {
    id: 2,
    chapter1: "Manhattan Chapter",
    chapter2: "Brooklyn Chapter",
    type: "Resource Sharing",
    status: "completed" as const,
    startDate: "2023-11-10",
    events: 5,
  },
  {
    id: 3,
    chapter1: "Houston Central Chapter",
    chapter2: "Austin Downtown Chapter",
    type: "Volunteer Exchange",
    status: "pending" as const,
    startDate: "2024-02-01",
    events: 0,
  },
];

// Simple HierarchyTree component
const HierarchyTree = () => {
  return (
    <Card>
      <div className="p-6 text-center text-muted-foreground">
        <Network size={48} className="mx-auto mb-4 text-muted-foreground" />
        <p>Hierarchy visualization will be implemented here</p>
      </div>
    </Card>
  );
};

// Simple ChaptersTable component
const ChaptersTable = ({
  chapters,
  onSendRequest,
  requestStatus,
}: {
  chapters: any[];
  onSendRequest: (chapter: any) => void;
  requestStatus: Record<number, string>;
}) => {
  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "srNo",
      key: "srNo",
    },
    {
      title: "Chapter Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
    },
    {
      title: "Active Members",
      dataIndex: "activeMembers",
      key: "activeMembers",
    },
    {
      title: "Events",
      dataIndex: "events",
      key: "events",
    },
    {
      title: "Active Sponsors",
      dataIndex: "activeSponsors",
      key: "activeSponsors",
    },
    {
      title: "Contact",
      key: "contact",
      render: (_: any, record: any) => (
        <div>
          <div>{record.contactName}</div>
          <div className="text-sm text-muted-foreground">
            {record.contactNumber}
          </div>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: any) => {
        const status = requestStatus[record.id] || record.requestStatus;
        if (status === "accepted") {
          return <Tag color="green">Accepted</Tag>;
        } else if (status === "sent") {
          return <Tag color="orange">Request Sent</Tag>;
        } else {
          return (
            <Button
              type="primary"
              size="small"
              onClick={() => onSendRequest(record)}
            >
              Send Request
            </Button>
          );
        }
      },
    },
  ];

  return <Table dataSource={chapters} columns={columns} rowKey="id" />;
};

// Simple CollaborationsTable component
const CollaborationsTable = ({ collaborations }: { collaborations: any[] }) => {
  const columns = [
    {
      title: "Chapter 1",
      dataIndex: "chapter1",
      key: "chapter1",
    },
    {
      title: "Chapter 2",
      dataIndex: "chapter2",
      key: "chapter2",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          ongoing: "blue",
          completed: "green",
          pending: "orange",
        };
        return (
          <Tag color={colorMap[status] || "default"}>
            {status.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
    },
    {
      title: "Events",
      dataIndex: "events",
      key: "events",
    },
  ];

  return <Table dataSource={collaborations} columns={columns} rowKey="id" />;
};

const Relationships = () => {
  const [sendRequestOpen, setSendRequestOpen] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState<any>(null);
  const [searchCommunity, setSearchCommunity] = useState("");
  const [searchLocation, setSearchLocation] = useState("all");
  const [chapterRequestStatus, setChapterRequestStatus] = useState<
    Record<number, string>
  >({});

  const handleSendRequest = (chapter: any) => {
    setSelectedChapter(chapter);
    setSendRequestOpen(true);
  };

  const handleRequestSent = (chapterId: number) => {
    setChapterRequestStatus(prev => ({ ...prev, [chapterId]: "sent" }));
  };

  const filteredChapters = mockChapters.filter(chapter => {
    const matchesCommunity = chapter.name
      .toLowerCase()
      .includes(searchCommunity.toLowerCase());
    const matchesLocation =
      searchLocation === "all" || chapter.location.includes(searchLocation);
    return matchesCommunity && matchesLocation;
  });

  return (
    <div className="space-y-4 p-2 sm:space-y-6 sm:p-4">
      {/* Header */}
      <div className="flex flex-col items-start justify-between sm:flex-row sm:items-center">
        <div>
          <h1 className="text-xl font-bold sm:text-2xl lg:text-3xl">
            Relationships & Collaboration
          </h1>
          <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
            Manage chapter hierarchies and collaborations
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 md:gap-6 lg:grid-cols-3">
        <StatCard
          title="Sent Requests"
          value="34"
          change="+8"
          changeType="increase"
          icon={Send}
          iconColor="text-primary"
        />
        <StatCard
          title="Connections Accepted"
          value="28"
          change="+12"
          changeType="increase"
          icon={CheckCircle2}
          iconColor="text-success"
        />
        <StatCard
          title="Hierarchy Status"
          value="156"
          change="+5"
          changeType="increase"
          icon={Network}
          iconColor="text-info"
        />
      </div>

      {/* Visual Hierarchy Tree */}
      <div>
        <h2 className="mb-3 text-lg font-semibold sm:mb-4 sm:text-xl">
          Hierarchy Visualization
        </h2>
        <HierarchyTree />
      </div>

      {/* Tabs for Chapters and Collaborations */}
      <div>
        <Tabs
          defaultActiveKey="chapters"
          items={[
            {
              key: "chapters",
              label: "Chapters",
              children: (
                <div className="space-y-4 sm:space-y-6">
                  {/* Search & Filters */}
                  <Card>
                    <div className="p-3 sm:p-4 md:p-6">
                      <div className="flex flex-col flex-wrap gap-3 sm:flex-row sm:gap-4">
                        <div className="min-w-[200px] flex-1 sm:min-w-[250px]">
                          <Input
                            placeholder="Search for Community..."
                            value={searchCommunity}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => setSearchCommunity(e.target.value)}
                            prefix={
                              <Search
                                size={16}
                                className="text-muted-foreground"
                              />
                            }
                          />
                        </div>
                        <Select
                          value={searchLocation}
                          onChange={(value: string) => setSearchLocation(value)}
                          style={{
                            width: "100%",
                            minWidth: 200,
                            maxWidth: 250,
                          }}
                          placeholder="Search for Location"
                        >
                          <Select.Option value="all">
                            All Locations
                          </Select.Option>
                          <Select.Option value="California">
                            California
                          </Select.Option>
                          <Select.Option value="New York">
                            New York
                          </Select.Option>
                          <Select.Option value="Texas">Texas</Select.Option>
                          <Select.Option value="Florida">Florida</Select.Option>
                        </Select>
                      </div>
                    </div>
                  </Card>

                  {/* Chapters Table */}
                  <ChaptersTable
                    chapters={filteredChapters}
                    onSendRequest={handleSendRequest}
                    requestStatus={chapterRequestStatus}
                  />
                </div>
              ),
            },
            {
              key: "collaborations",
              label: "Collaborations",
              children: (
                <CollaborationsTable collaborations={mockCollaborations} />
              ),
            },
          ]}
        />
      </div>

      {/* Send Request Modal */}
      <Modal
        title="Send Request"
        open={sendRequestOpen}
        onCancel={() => setSendRequestOpen(false)}
        onOk={() => {
          if (selectedChapter) {
            handleRequestSent(selectedChapter.id);
            setSendRequestOpen(false);
          }
        }}
        footer={null}
      >
        <p>
          Send request form will be implemented here. Chapter:{" "}
          {selectedChapter?.name}
        </p>
      </Modal>
    </div>
  );
};

export default Relationships;
